import { domain } from "../auth/domain";

/**
 * Centralized API helper for loan endpoints.
 * It preserves parsed backend validation payloads for richer OTP UX.
 */
const apiRequest = async (endpoint, options = {}) => {
  const accessToken = localStorage.getItem("accessToken");
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (accessToken) {
    headers.Authorization = `JWT ${accessToken}`;
  }

  const response = await fetch(`${domain}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData = null;
    let errorMessage = response.statusText || "An unknown error occurred.";

    try {
      errorData = await response.json();
      if (errorData?.detail) {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData)) {
        errorMessage = errorData.join(" ");
      } else if (typeof errorData === "object" && errorData !== null) {
        errorMessage = Object.entries(errorData)
          .map(([field, errors]) => {
            const normalized = Array.isArray(errors) ? errors.join(" ") : errors;
            return `${field}: ${normalized}`;
          })
          .join("\n");
      }
    } catch (error) {
      errorData = null;
    }

    const requestError = new Error(`API request failed: ${errorMessage}`);
    requestError.status = response.status;
    requestError.payload = errorData;
    throw requestError;
  }

  if (response.status === 204) {
    return null;
  }
  return response.json();
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.append(key, value);
  });
  return query.toString();
};

export const getLoans = (params = {}) => {
  const query = buildQuery(params);
  return apiRequest(query ? `loans/loans/?${query}` : "loans/loans/");
};

export const getMemberLoans = (memberId, params = {}) => {
  if (!memberId) {
    throw new Error("Member ID is required to fetch loans.");
  }
  const query = buildQuery({ member_id: memberId, ...params });
  return apiRequest(`loans/loans/?${query}`);
};

/**
 * Initiates a loan request and returns pending OTP challenge metadata.
 */
export const createLoan = (loanData) =>
  apiRequest("loans/loans/", {
    method: "POST",
    body: JSON.stringify(loanData),
  });

export const verifyLoanOtp = (payload) =>
  apiRequest("loans/loans/verify-otp/", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const resendLoanOtp = (payload) =>
  apiRequest("loans/loans/resend-otp/", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const processLoanApproval = (loanId, action, reason) => {
  const payload = action === "reject" && reason ? { rejection_reason: reason } : {};
  return apiRequest(`loans/loans/${loanId}/approve/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const requestPenaltyWaiver = (waiverData) =>
  apiRequest(`loans/penalty-waivers/`, {
    method: "POST",
    body: JSON.stringify(waiverData),
  });

export const processWaiverApproval = (waiverId, rejectionReason) => {
  const payload = rejectionReason ? { rejection_reason: rejectionReason } : {};
  return apiRequest(`loans/penalty-waivers/${waiverId}/approve/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};
